'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import clsx from 'clsx';
import { RATING_REVIEWS_MENU_ID } from '@headless-workspace/config';
import { useUIContext } from '@headless-workspace/core-ui';
import { ConnectedUserInfo, RatingAndReviewsFormValue } from '@headless-workspace/domain/common/server';
import {
    GlowClickable,
    GlowIcon,
    GlowImage,
    GlowTextBody,
    GlowTitle,
    Icons,
    PropsWithLocale,
} from '@headless-workspace/glow-ds';
import { Component, ImageWithSrc } from '@headless-workspace/typings';
import { RatingsAndReviewsData } from '../../../../server';
import { RatingsAndReviewsForm } from './RatingsAndReviewsForm';
import { RatingsAndReviewsFormInformation } from './RatingsAndReviewsFormInformation';

type RatingsAndReviewsModalProps = PropsWithLocale & {
    productId: string;
    userInfo: ConnectedUserInfo;
    ratingAndReviewsForm: RatingAndReviewsFormValue;
};

const IMG_WIDTH = 96;
const IMG_HEIGHT = 96;

const ModalHeader = ({
    title,
    canGoBack,
    onClose,
    onGoBack,
}: {
    title: string;
    canGoBack: boolean;
    onGoBack: () => void;
    onClose: () => void;
}) => (
    <div
        className={
            'flex flex-row justify-between items-center p-1 gap-1 border-b-medium border-b-border-subtle border-solid h-headerFixedNavDesktop'
        }
    >
        <div className={'flex flex-row gap-1 items-center'}>
            {canGoBack && (
                <GlowClickable
                    onClick={onGoBack}
                    content={{
                        icon: (
                            <GlowIcon
                                Icon={Icons.ChevronRight}
                                type={'large'}
                                className={'text-text-primary rotate-180'}
                            />
                        ),
                    }}
                />
            )}
            <GlowTitle text={title} fontSize={'title4'} className={'flex-1 text-left'} />
        </div>
        <GlowClickable
            onClick={onClose}
            content={{
                icon: <GlowIcon Icon={Icons.Cross} type={'large'} className={'text-text-primary'} />,
            }}
        />
    </div>
);

const ProductInfo = ({
    name,
    brand,
    image,
    isSubmitted,
    title,
}: {
    name: string;
    brand: string;
    image?: ImageWithSrc;
    isSubmitted: boolean;
    title: string;
}) => (
    <div className={'flex flex-col gap-1'}>
        {isSubmitted && <GlowTextBody fontWeight={'bold'} text={title} TagName={'h4'} />}
        <div className={'flex flex-row gap-1 items-center'}>
            {image && (
                <GlowImage
                    className={'p-0.125 border-medium border-solid rounded-0.5 border-border-primary'}
                    image={image}
                    width={IMG_WIDTH}
                    height={IMG_HEIGHT}
                />
            )}
            <div className={'flex flex-col'}>
                <GlowTextBody text={name} />
                <GlowTextBody text={brand} fontWeight={'bold'} />
            </div>
        </div>
    </div>
);

export const RatingsAndReviewsModal: Component<RatingsAndReviewsModalProps> = ({
    productId,
    userInfo,
    locale,
    ratingAndReviewsForm,
}) => {
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [ratingsAndReviewsData, setRatingsAndReviewsData] = useState<RatingsAndReviewsData | undefined>();
    const t = useTranslations('Discover.Pdp.ratingAndReviewsForm');
    const { menuModal } = useUIContext();
    const userCard = userInfo?.loyalty?.sephoraCard;

    const closeRatingsAndReviewsModal = useCallback(() => menuModal.close(RATING_REVIEWS_MENU_ID), [menuModal]);

    const handlePostSubmit = useCallback(() => {
        closeRatingsAndReviewsModal();
        setIsSubmitted(false);
        setRatingsAndReviewsData(undefined);
    }, [closeRatingsAndReviewsModal]);

    useEffect(() => {
        setIsSubmitted(false);
    }, [productId]);

    const handleSubmitForm = useCallback((isSuccess: boolean, data: RatingsAndReviewsData) => {
        setIsSubmitted(isSuccess);
        setRatingsAndReviewsData(data);
    }, []);

    const { product, fields } = ratingAndReviewsForm;

    const ProductInfosContent = useMemo(
        () => (
            <ProductInfo
                isSubmitted={isSubmitted}
                name={product.name}
                brand={product.brand.name}
                image={product.image}
                title={t('label.overviewTitle')}
            />
        ),
        [isSubmitted, product, t],
    );

    return (
        <div className={'h-fullViewport w-fullViewport tablet:w-sideMenuTablet flex flex-col bg-background-l3'}>
            <ModalHeader
                canGoBack={isSubmitted}
                title={t('label.modalTitle')}
                onClose={closeRatingsAndReviewsModal}
                onGoBack={() => setIsSubmitted(false)}
            />

            <div className={clsx('flex flex-1 flex-col overflow-x-auto')}>
                {!isSubmitted ? (
                    <RatingsAndReviewsForm
                        formInfo={fields}
                        onSubmit={handleSubmitForm}
                        locale={locale}
                        userInfo={userInfo}
                        ratingsAndReviewsData={ratingsAndReviewsData}
                        ProductInfos={ProductInfosContent}
                    />
                ) : (
                    <RatingsAndReviewsFormInformation
                        gender={userInfo.gender}
                        locale={locale}
                        userCard={userCard}
                        productId={productId}
                        ratingsAndReviewsData={ratingsAndReviewsData}
                        acceptedTermsAndConditions={ratingAndReviewsForm.fields.acceptedTermsAndConditions}
                        age={
                            ratingAndReviewsForm.fields.age.options.find(
                                (option) => option.value === ratingsAndReviewsData?.age,
                            )?.label
                        }
                        onConfirm={handlePostSubmit}
                        ProductInfos={ProductInfosContent}
                    />
                )}
            </div>
        </div>
    );
};
